import { useState } from 'react';
import { RiAddLine } from 'react-icons/ri';
import type { Resume } from '../../models/v2/profile';
import { resumeService } from '../../services/resumeService';
import { useConfirm } from '../../context/ConfirmContext';
import { useProfileMutation, useProfile } from '../../context/ProfileContext';
import GlassCard from '../ui/v2/GlassCard';
import ResumeCard from './ResumeCard';
import UploadResumeModal from './UploadResumeModal';
import ResumePreviewModal from './ResumePreviewModal';

interface Props {
    resumes: Resume[];
}

export default function ResumeSection({ resumes: initialResumes }: Props) {
    const { updateLocalState } = useProfile();
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // Mutations using global context
    const { mutate: uploadResume } = useProfileMutation<File>();
    const { mutate: deleteResume } = useProfileMutation<string>();
    const { mutate: setDefaultResume } = useProfileMutation<string>();

    const handleUpload = async (file: File, isDefault: boolean) => {
        await uploadResume(file, {
            mutationFn: (f) => resumeService.upload(f, undefined, isDefault),
            successMessage: "Resume uploaded successfully!",
            onSuccess: (newResume) => {
                updateLocalState(prev => {
                    if (!prev) return prev;
                    return { ...prev, resumes: [...prev.resumes, newResume] };
                });
                setIsUploadOpen(false);
            }
        });
    };

    const handleSetDefault = async (resume: Resume) => {
        // Optimistic update
        updateLocalState(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                resumes: prev.resumes.map(r => ({
                    ...r,
                    is_default: r.id === resume.id
                }))
            };
        });

        await setDefaultResume(resume.id, {
            mutationFn: (id) => resumeService.setDefault(id),
            successMessage: "Default resume updated",
            onError: () => {
                // Revert is handled by forcing a refresh or user retry
                // Ideally, we could revert state here if we had prev state snapshot
                console.error("Failed to set default");
            }
        });
    };

    const { ask } = useConfirm();

    const handleDelete = async (resume: Resume) => {
        const confirmed = await ask({
            title: "Delete Resume",
            message: "Are you sure you want to delete this resume? This action cannot be undone.",
            confirmText: "Delete Resume",
            variant: "danger"
        });

        if (!confirmed) return;

        await deleteResume(resume.id, {
            mutationFn: (id) => resumeService.deleteResume(id),
            successMessage: "Resume deleted successfully",
            onSuccess: () => {
                updateLocalState(prev => {
                    if (!prev) return prev;
                    return { ...prev, resumes: prev.resumes.filter(r => r.id !== resume.id) };
                });
            }
        });
    };

    // Sort: Newest first
    const sortedResumes = [...initialResumes].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return (
        <GlassCard>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white">Resume</h2>
                <button
                    onClick={() => setIsUploadOpen(true)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300 transition-colors text-sm font-medium border border-blue-500/20"
                >
                    <RiAddLine />
                    Upload Resume
                </button>
            </div>

            <div className="space-y-3">
                {sortedResumes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-white/5 rounded-xl bg-white/[0.02]">
                        <p className="text-zinc-500 text-sm mb-4">No resumes uploaded yet.</p>
                        <button
                            onClick={() => setIsUploadOpen(true)}
                            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                        >
                            Upload your first resume
                        </button>
                    </div>
                ) : (
                    sortedResumes.map(resume => (
                        <ResumeCard
                            key={resume.id}
                            resume={resume}
                            onPreview={(r) => setPreviewUrl(r.file_url)}
                            onSetDefault={handleSetDefault}
                            onDelete={handleDelete}
                        />
                    ))
                )}
            </div>

            <UploadResumeModal
                isOpen={isUploadOpen}
                onClose={() => setIsUploadOpen(false)}
                onUpload={handleUpload}
                isFirstResume={initialResumes.length === 0}
            />

            <ResumePreviewModal
                url={previewUrl}
                isOpen={!!previewUrl}
                onClose={() => setPreviewUrl(null)}
            />
        </GlassCard>
    );
}
